import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import { Document, Packer, Paragraph, HeadingLevel } from "docx";
import { saveAs } from "file-saver";
import { config } from "../config";
import { analytics } from "../components/firebase";
import { logEvent } from "firebase/analytics";
import Leftpanel from "../components/leftPanel";
import "./Generatepage.css";
import { Snackbar } from "@mui/material";
import { Alert } from "@mui/material";

import { franc } from 'franc-min';
const HistoryPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isSnackbarOpen, setSnackbarOpen] = useState(false);
  const menuRef = useRef();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState("");
  const originalTextRef = useRef("");
  
  const result = location.state?.result || "No result available.";
  const historyId = location.state?.history_id;

  const [displayedText, setDisplayedText] = useState("");
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("success");

  const [isSpeaking, setIsSpeaking] = useState(false);
  const utteranceRef = useRef(null);

  const langMap = {
    eng: "en-US",
    arb: "ar-SA",
    bul: "bg-BG",
    cmn: "zh-CN",
    fra: "fr-FR",
    deu: "de-DE",
    heb: "he-IL",
    hin: "hi-IN",
    ind: "id-ID",
    ita: "it-IT",
    jpn: "ja-JP",
    por: "pt-PT",
    pan: "pa-IN",
    rus: "ru-RU",
    spa: "es-ES",
    swa: "sw-KE",
    tgl: "tl-PH",
    urd: "ur-PK",
  };
  
  const darkmode = () => {
    setIsDarkMode((prevMode) => !prevMode);    
  };

  const showNotification = (msg, severity = "success") => {
    setMessage(msg);
    setSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleClose = () => {
    setSnackbarOpen(false);
  };
  useEffect(() => {
    document.documentElement.style.setProperty("--main-color", config.color);
  }, []);

  useEffect(() => {
    if (!displayedText) {
      setDisplayedText(result);
      setEditedText(result);
      originalTextRef.current = result;
    }
  }, []);

  const toggleTTS = () => {
    const text = document.getElementById("generate-document-content")?.innerText;
    if (!text?.trim()) return;
  
    if (speechSynthesis.speaking) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }
  
    const langCode = franc(text);
    const voiceLang = langMap[langCode] || "en-US";
  
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = voiceLang;
    utterance.onend = () => setIsSpeaking(false);
  
    utteranceRef.current = utterance;
    speechSynthesis.speak(utterance);
    setIsSpeaking(true);
  };

  useEffect(() => {
    const fetchUpdatedText = async () => {
      if (!historyId) return;

      try {
        const response = await fetch(
          `${config.baseURL}/getUpdatedHistory.php`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ history_id: historyId }),
          }
        );

        const data = await response.json();

        if (data.success) {
          setDisplayedText(data.content);
          setEditedText(data.content);
          originalTextRef.current = data.content;
        } else {
          showNotification("Failed to load content.", "error");
        }
      } catch (err) {
 
        showNotification("Error loading data.", "error");
      }
    };

    fetchUpdatedText();
  }, [historyId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener("mousedown", handleClickOutside);
    return () => window.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDocTitle = () => {
    const lines = displayedText.split("\n");
    const titleWords = lines[0]?.trim().split(/\s+/).slice(0, 3) || [
      "document",
    ];
    const title = titleWords.join(" ");
    return title.replace(/[^a-zA-Z0-9_-]/g, "_");
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(displayedText);
    showNotification("Copied to clipboard ✅");
    logEvent(analytics, "legal_docaction", {
      action: "copy_legal_doccontent",
      button_name: "copy_button",
      source: "history_preview",
    });
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Generated Academics",
          text: displayedText,
        });
        showNotification("Text shared successfully ✅");
        logEvent(analytics, "legal_docaction", {
          action: "share_legal_doccontent",
          button_name: "share_button",
          source: "history_preview",
        });
      } else {
        throw new Error("Share not supported");
      }
    } catch (error) {
      
      showNotification("Sharing failed ❌", "error");
    }
  };

  const handleSavePdf = () => {
    const docTitle = getDocTitle();
    const pdf = new jsPDF();
    pdf.setFontSize(12);
    const lines = pdf.splitTextToSize(displayedText, 180);
    pdf.text(lines, 10, 10);
    pdf.save(`${docTitle}.pdf`);

    showNotification("PDF saved successfully ✅");
    logEvent(analytics, "legal_docaction", {
      action: "save_as_pdf",
      button_name: "save_pdf",
      source: "history_preview",
    });
  };

  const handleSaveDocx = async () => {
    const docTitle = getDocTitle();
    const allParagraphs = displayedText
      .split("\n")
      .map((line) => new Paragraph(line));

    const doc = new Document({
      sections: [
        {
          properties: {},
          children: allParagraphs,
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${docTitle}.docx`);
    showNotification("DOCX saved successfully ✅");
    logEvent(analytics, "legal_docaction", {
      action: "save_as_docx",
      button_name: "save_docx",
      source: "history_preview",
    });
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`${config.baseURL}/update_history.php`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          history_id: historyId,
          updated_text: editedText,
        }),
      });

      if (!response.ok) throw new Error("Failed to update text");

      setDisplayedText(editedText);
      originalTextRef.current = editedText;
      setIsEditing(false);
      showNotification("Text updated successfully ✅", "success");
    } catch (err) {
   
      showNotification(
        "Something went wrong. Please try again later.",
        "error"
      );
      setIsEditing(false);
    }
  };

  return (
    <>
      <Leftpanel />
      <div className="generate-page-container">
        <div className="generate-topdiv"  style={{backgroundColor: isDarkMode ? "black" : "white",color: isDarkMode ? "white" : "black", }} >
          <div className="backbtntext">
            <button
            
              className="back-button"
              title="Back"
              onClick={() => {
                if (isEditing) {
                  setIsEditing(false);
                  setEditedText(displayedText);
                  return;
                }
                navigate(-1);
              }}
            >
              <img src={isDarkMode ? config.backButtonWhite : config.Backbutton} alt="Back" />
            </button>
            <span  style={{color: isDarkMode ? "white" : "black", }}>Result</span>
          </div>

          {isDarkMode && (

              <img
              src={isDarkMode ? config.crossimgWhite: config.crossimg}
              title="Copy text"
              alt="Copy"
              onClick={darkmode}
              style={{ cursor: "pointer" }}
              />
              )}
          {!isDarkMode && (
            <div>
          {!isEditing && (
            <div className="button-container">
                <img src={config.proIcon} alt="Pro" onClick={() => {navigate('/premium');}} />
               
              <img
                src={config.copyButtonWB}
                title="Copy text"
                alt="Copy"
                onClick={handleCopy}
                style={{ cursor: "pointer" }}
              />
              <img
                src={config.shareButtonWB}
                alt="Share"
                title="Share text"
                onClick={handleShare}
                style={{ cursor: "pointer" }}
              />
              <div className="menu-wrapper_generate" ref={menuRef}>
                <img
                  src={config.menuDropDown}
                  alt="More"
                  title="More"
                  className="menu-trigger_generate"
                  style={{ cursor: "pointer" }}
                  onClick={() => setDropdownOpen((o) => !o)}
                />
                {isDropdownOpen && (
                  <div className="menu-dropdown_generate">
                    <button onClick={handleCopy}>Copy Text</button>
                    <button onClick={handleShare}>Share Text</button>
                    <button onClick={handleSavePdf}>Save as PDF</button>
                    <button onClick={handleSaveDocx}>Save as DOCX</button>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
        )}
        </div>
        
        {!isEditing ? (
          <pre
            id="generate-document-content"
            style={{
              whiteSpace: "pre-wrap",
              fontFamily: "Noto Sans",
              borderRadius: "5px",
              backgroundColor: isDarkMode ? "black" : "white", 
              color: isDarkMode ? "white" : "black", 
            }}
          >
            {displayedText}
          </pre>
        ) : (
          <textarea
            id="generate-document-content"
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            rows={10}
            style={{ width: "100%", height: "100vh" }}
          />
        )}

        
        <Snackbar
          open={isSnackbarOpen}
          autoHideDuration={2000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={severity} sx={{ width: "100%", borderRadius: 2 }}>
            {message}
          </Alert>
        </Snackbar>
        {!isDarkMode && (
        <div className="bottomBar">
                  <img src={config.proIcon} alt="Pro" onClick={() => {navigate('/premium');}} />
                  <div className="bottomBarButtons">
                  {!isEditing && ( 
                    <>
                      <button ><img src={config.pdfRed} alt="pdf" /></button>
                      <button onClick={toggleTTS}>
                        <img
                          src={isSpeaking ? config.stopTTS : config.playTTS}
                          alt={isSpeaking ? "Pause TTS" : "Listen to TTS"}
                        />                      
                      </button>
                      <button>  <img
                src={config.darkMode}
                alt="dark"
                title="Share text"
                onClick={darkmode}
                style={{ cursor: "pointer" }}
              /></button>
                    </>
                  )}
                  {!isEditing ? (
                    <button className="editButton" onClick={() => setIsEditing(true)}>
                      <img src={config.editIcon} alt="edit" />
                      Edit
                    </button>
                  ) : (
                    <button className="editButton" onClick={handleSaveEdit}>
                      Save
                    </button>
                  )}
                </div>
                </div>
                 )}
      </div>
      
    </>
  );
};

export default HistoryPreview;
